import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import { cn } from '@Utils/cn';
import {
  AlertTriangle,
  Check,
  ChevronDown,
  CalendarPlus,
  Loader2,
  Mail,
  MessageSquare,
  Plus,
  Tag,
  Tags,
  Users,
  X,
} from 'lucide-react';
import service from '@Service';
import type { ActionCardPayload, BusinessObjectMap, MergeTag } from '@Types/soul';

export type CardStatus = 'idle' | 'loading' | 'success' | 'error' | 'cancelled';

interface Props {
  payload: ActionCardPayload;
  businessObjects: BusinessObjectMap;
  chatId: string | null;
  messageId?: string;
  actionIndex?: number;
  initialStatus?: CardStatus;
  initialResult?: string;
  initialEventId?: string;
  onActionExecuted?: (message: { id: string; content: string }, sourceMessageId?: string, actionIndex?: number, status?: CardStatus) => void;
}

const ACTION_CONFIG: Record<
  string,
  { icon: React.ElementType; label: string; color: string }
> = {
  'send-message': { icon: MessageSquare, label: 'Send Message', color: 'blue' },
  'bulk-tag': { icon: Tags, label: 'Bulk Tag', color: 'purple' },
  'add-tag': { icon: Tag, label: 'Add Tag', color: 'purple' },
  'create-tag': { icon: Plus, label: 'Create Tag', color: 'green' },
  'create-and-tag': { icon: Tags, label: 'Create & Tag', color: 'purple' },
  'create-segment': { icon: Users, label: 'Create Segment', color: 'amber' },
  'create-ticketed-event': { icon: CalendarPlus, label: 'Create Event', color: 'rose' },
};

const COLOR_CLASSES: Record<string, { bg: string; border: string; iconBg: string; text: string }> = {
  blue: {
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    iconBg: 'bg-blue-100 text-blue-600',
    text: 'text-blue-700',
  },
  purple: {
    bg: 'bg-purple-50',
    border: 'border-purple-200',
    iconBg: 'bg-purple-100 text-purple-600',
    text: 'text-purple-700',
  },
  green: {
    bg: 'bg-green-50',
    border: 'border-green-200',
    iconBg: 'bg-green-100 text-green-600',
    text: 'text-green-700',
  },
  amber: {
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    iconBg: 'bg-amber-100 text-amber-600',
    text: 'text-amber-700',
  },
  rose: {
    bg: 'bg-rose-50',
    border: 'border-rose-200',
    iconBg: 'bg-rose-100 text-rose-600',
    text: 'text-rose-700',
  },
};

// ── Merge Tag Picker ──

function MergeTagPicker({ onInsert }: { onInsert: (tagId: string) => void }) {
  const [open, setOpen] = useState(false);
  const [mergeTags, setMergeTags] = useState<MergeTag[]>([]);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    service.getMergeTags()
      .then((res: { data: MergeTag[] }) => setMergeTags(res.data ?? []))
      .catch(() => {});
  }, []);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  if (mergeTags.length === 0) return null;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1 rounded border border-zinc-300 bg-white px-2 py-1 text-xs text-zinc-600 hover:bg-zinc-50"
      >
        <span>Insert merge tag</span>
        <ChevronDown className={cn('size-3 transition-transform', open && 'rotate-180')} />
      </button>
      {open && (
        <div className="absolute bottom-full left-0 z-10 mb-1 max-h-48 w-56 overflow-y-auto rounded-md border border-zinc-200 bg-white py-1 shadow-lg">
          {mergeTags.map((tag) => (
            <button
              key={tag.id}
              type="button"
              className="flex w-full items-center justify-between px-3 py-1.5 text-left text-xs hover:bg-blue-50"
              onClick={() => {
                onInsert(tag.id);
                setOpen(false);
              }}
            >
              <span className="text-zinc-700">{tag.label}</span>
              <code className="text-[10px] text-zinc-400">[[{tag.id}]]</code>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Action Card ──

export default function ActionCard({ payload, businessObjects, chatId, messageId, actionIndex, initialStatus, initialResult, initialEventId, onActionExecuted }: Props) {
  const [status, setStatus] = useState<CardStatus>(initialStatus ?? 'idle');
  const [errorMsg, setErrorMsg] = useState(initialStatus === 'error' ? (initialResult || 'Action failed') : '');
  const [result, setResult] = useState<string>(initialStatus === 'success' ? (initialResult || 'Action completed successfully') : '');
  const [editedBody, setEditedBody] = useState(payload.messageBody ?? '');
  const [resultEventId, setResultEventId] = useState<string | null>(initialEventId ?? null);
  const [publishing, setPublishing] = useState(false);
  const [published, setPublished] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Reflect the event's real published state across reloads — the Publish
  // button writes directly to the event (not the chat), so we read its actual
  // status by ID rather than persisting a flag in chat history.
  useEffect(() => {
    if (status !== 'success' || !resultEventId || published) return;
    let cancelled = false;
    service
      .getTicketedEvent(resultEventId)
      .then((res: any) => {
        const eventStatus = res?.status ?? res?.data?.status;
        if (!cancelled && eventStatus === 'PUBLISHED') setPublished(true);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [status, resultEventId, published]);

  const config = ACTION_CONFIG[payload.type] ?? ACTION_CONFIG['create-tag'];
  const colors = COLOR_CLASSES[config.color] ?? COLOR_CLASSES.blue;
  const Icon = config.icon;

  const bo = payload.boKey ? businessObjects[payload.boKey] : null;
  const memberCount = bo?.count ?? 0;

  const canConfirm = chatId && status === 'idle';
  const isSendMessage = payload.type === 'send-message';

  const insertMergeTag = (tagId: string) => {
    const textarea = textareaRef.current;
    const tag = `[[${tagId}]]`;
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const newValue = editedBody.slice(0, start) + tag + editedBody.slice(end);
      setEditedBody(newValue);
      // Restore cursor after the inserted tag
      requestAnimationFrame(() => {
        textarea.focus();
        const pos = start + tag.length;
        textarea.setSelectionRange(pos, pos);
      });
    } else {
      setEditedBody((prev) => prev + tag);
    }
  };

  const handleConfirm = async () => {
    if (!chatId) return;
    setStatus('loading');
    setErrorMsg('');

    try {
      const isUuid = messageId && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(messageId);
      const executePayload = {
        ...payload,
        // Use the edited message body for send-message
        ...(isSendMessage ? { messageBody: editedBody } : {}),
        ...(isUuid ? { sourceMessageId: messageId } : {}),
        ...(isUuid && actionIndex != null ? { actionIndex } : {}),
      };
      const res = await service.executeAction(chatId, executePayload);
      setStatus('success');
      const resultText = formatResult(payload.type, res);
      setResult(resultText);
      if (payload.type === 'create-ticketed-event' && res.data?.eventId) {
        setResultEventId(res.data.eventId);
      }
      if (onActionExecuted && res.message) {
        onActionExecuted(res.message, messageId, actionIndex, 'success');
      }
    } catch (err: any) {
      setStatus('error');
      setErrorMsg(err.message || 'Action failed');
      if (onActionExecuted) {
        onActionExecuted(
          { id: '', content: err.message || 'Action failed' },
          messageId,
          actionIndex,
          'error'
        );
      }
    }
  };

  const handleRetry = () => {
    setStatus('idle');
    setErrorMsg('');
  };

  const handlePublish = async () => {
    if (!resultEventId) return;
    setPublishing(true);
    try {
      await service.updateTicketedEvent(resultEventId, {
        event: { status: 'PUBLISHED' },
      });
      setPublished(true);
    } catch {
      // Keep the button so the user can retry
    } finally {
      setPublishing(false);
    }
  };

  return (
    <div
      className={cn(
        'my-3 rounded-lg border p-4',
        colors.bg,
        colors.border,
        status === 'success' && 'border-green-300 bg-green-50',
        status === 'error' && 'border-red-300 bg-red-50',
      )}
    >
      {/* Header */}
      <div className="mb-2 flex items-center gap-2">
        <div className={cn('rounded-md p-1.5', colors.iconBg)}>
          <Icon className="size-4" />
        </div>
        <span className={cn('text-sm font-semibold', colors.text)}>
          {config.label}
        </span>
      </div>

      {/* Body */}
      <div className="mb-3 space-y-1.5 text-sm text-zinc-700">
        {isSendMessage
          ? renderSendMessageBody(payload, memberCount, editedBody, setEditedBody, textareaRef, insertMergeTag, status)
          : renderBody(payload, memberCount)}
      </div>

      {/* Status-specific UI */}
      {status === 'success' && (
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-sm text-green-700">
            <Check className="size-4" />
            <span>{result || 'Action completed successfully'}</span>
          </div>
          {resultEventId && (
            <div className="flex items-center gap-3">
              <Link
                to={`/ticketed-events/${resultEventId}`}
                className="inline-flex items-center gap-1 text-sm font-medium text-rose-600 hover:text-rose-700 hover:underline"
              >
                View event →
              </Link>
              {published ? (
                <span className="inline-flex items-center gap-1 text-sm font-medium text-green-600">
                  <Check className="size-3.5" />
                  Published
                </span>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handlePublish}
                  loading={publishing}
                >
                  Publish
                </Button>
              )}
            </div>
          )}
        </div>
      )}

      {status === 'error' && (
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-sm text-red-700">
            <AlertTriangle className="size-4" />
            <span>{errorMsg}</span>
          </div>
          <Button variant="outline" size="sm" onClick={handleRetry}>
            Retry
          </Button>
        </div>
      )}

      {status === 'cancelled' && (
        <div className="flex items-center gap-2 text-sm text-zinc-500">
          <X className="size-4" />
          <span>Cancelled</span>
        </div>
      )}

      {status === 'loading' && (
        <div className="flex items-center gap-2 text-sm text-zinc-500">
          <Loader2 className="size-4 animate-spin" />
          <span>Executing...</span>
        </div>
      )}

      {/* Action buttons */}
      {status === 'idle' && (
        <div className="flex items-center gap-2">
          <Button
            variant="primary"
            size="sm"
            onClick={handleConfirm}
            disabled={!canConfirm || (isSendMessage && !editedBody.trim())}
          >
            <Check className="mr-1 size-3.5" />
            Confirm
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setStatus('cancelled')}
            disabled={!canConfirm}
          >
            <X className="mr-1 size-3.5" />
            Cancel
          </Button>
          {payload.boKey && !bo && payload.type !== 'create-ticketed-event' && (
            <span className="text-xs text-red-500">
              Data set not found — re-run the search
            </span>
          )}
        </div>
      )}
    </div>
  );
}

// ── Send Message body (editable) ──

function renderSendMessageBody(
  payload: ActionCardPayload,
  memberCount: number,
  editedBody: string,
  setEditedBody: (v: string) => void,
  textareaRef: React.RefObject<HTMLTextAreaElement | null>,
  insertMergeTag: (tagId: string) => void,
  status: CardStatus,
) {
  const MethodIcon = payload.method === 'EMAIL' ? Mail : MessageSquare;
  const isEditable = status === 'idle';

  return (
    <>
      <div className="flex items-center gap-1.5">
        <MethodIcon className="size-3.5 text-zinc-500" />
        <span className="font-medium">{payload.method}</span>
        {memberCount > 0 && (
          <span className="text-zinc-500">to {memberCount} members</span>
        )}
      </div>
      {payload.messageSubject && (
        <div className="text-zinc-500">
          Subject: <span className="text-zinc-700">{payload.messageSubject}</span>
        </div>
      )}
      {isEditable ? (
        <div className="mt-1 space-y-1.5">
          <textarea
            ref={textareaRef}
            value={editedBody}
            onChange={(e) => setEditedBody(e.target.value)}
            rows={4}
            className="w-full rounded border border-zinc-300 bg-white p-2 text-xs text-zinc-700 focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400"
            placeholder="Type your message..."
          />
          <MergeTagPicker onInsert={insertMergeTag} />
        </div>
      ) : (
        <div className="mt-1 rounded border border-zinc-200 bg-white p-2 text-xs text-zinc-600">
          {editedBody}
        </div>
      )}
    </>
  );
}

// ── Other action bodies (read-only) ──

function renderBody(
  payload: ActionCardPayload,
  memberCount: number,
) {
  switch (payload.type) {
    case 'bulk-tag':
      return (
        <>
          <div className="flex items-center gap-1.5">
            <Tags className="size-3.5 text-zinc-500" />
            <span>
              Apply tag <span className="font-medium">"{payload.tagName}"</span>
            </span>
          </div>
          {memberCount > 0 && (
            <div className="flex items-center gap-1.5 text-zinc-500">
              <Users className="size-3.5" />
              <span>{memberCount} members</span>
            </div>
          )}
        </>
      );

    case 'add-tag':
      return (
        <div className="flex items-center gap-1.5">
          <Tag className="size-3.5 text-zinc-500" />
          <span>
            Tag <span className="font-medium">{payload.fishName}</span> as{' '}
            <span className="font-medium">"{payload.tagName}"</span>
          </span>
        </div>
      );

    case 'create-tag':
      return (
        <>
          <div>
            Create tag: <span className="font-medium">"{payload.tagName}"</span>
          </div>
          {payload.description && (
            <div className="text-xs text-zinc-500">{payload.description}</div>
          )}
        </>
      );

    case 'create-and-tag':
      return (
        <>
          <div className="flex items-center gap-1.5">
            <Plus className="size-3.5 text-zinc-500" />
            <span>
              Create tag <span className="font-medium">"{payload.tagName}"</span> and apply
            </span>
          </div>
          {memberCount > 0 && (
            <div className="flex items-center gap-1.5 text-zinc-500">
              <Users className="size-3.5" />
              <span>{memberCount} members</span>
            </div>
          )}
        </>
      );

    case 'create-segment':
      return (
        <div>
          Create segment: <span className="font-medium">"{payload.segmentName}"</span>
        </div>
      );

    case 'create-ticketed-event':
      return (
        <div className="flex items-center gap-1.5">
          <CalendarPlus className="size-3.5 text-zinc-500" />
          <span>
            Create event:{' '}
            <span className="font-medium">"{payload.eventName ?? 'Draft event'}"</span>
          </span>
        </div>
      );

    default:
      return <div>Unknown action type: {payload.type}</div>;
  }
}

function formatResult(type: string, res: any): string {
  switch (type) {
    case 'send-message':
      return `Scheduled ${res.data?.scheduled ?? ''} messages for delivery`;
    case 'bulk-tag':
      return `Tagged ${res.data?.tagged ?? 0} members (${res.data?.skipped ?? 0} already tagged)`;
    case 'add-tag':
      return 'Tag applied successfully';
    case 'create-tag':
      return `Tag "${res.data?.name ?? ''}" created`;
    case 'create-and-tag':
      return `Created tag "${res.data?.tagName ?? ''}" and tagged ${res.data?.tagged ?? 0} members (${res.data?.skipped ?? 0} already tagged)`;
    case 'create-segment':
      return `Segment "${res.data?.name ?? ''}" created`;
    case 'create-ticketed-event':
      return `Event "${res.data?.eventName ?? ''}" created (${res.data?.status ?? 'DRAFT'})`;
    default:
      return 'Action completed';
  }
}
